// apps/frontend/admin-ui/src/pages/rules.ts
import { AdminApiError, type AdminClient, type RulesOverride } from '@apx/admin-sdk';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { prettyGame } from '../games.js';
import { rulesSchema } from '@apx/integrator-config/schemas';

export interface RulesPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

const LOCALES = ['en', 'id', 'th', 'tl', 'vi'] as const;
type SupportedLocale = (typeof LOCALES)[number];

/** Last-resort fallback markdown if the per-game default endpoint is unreachable. */
const GENERIC_FALLBACK_MD =
  '# Rules\n\nEach round, bets are placed on the available outcomes. See the Payouts tab for odds.';

/** Fetch the canonical per-game default markdown for ONE locale from the
 *  round-engine public endpoint. The `?locale=` param is required so each
 *  locale seeds its own game-specific prose. Falls back to GENERIC_FALLBACK_MD
 *  on any error so the UI always has something to show. */
async function fetchDefaultRulesMarkdown(
  roundEngineBaseUrl: string,
  integratorId: string,
  gameId: string,
  locale: string,
): Promise<string> {
  try {
    const res = await fetch(
      `${roundEngineBaseUrl}/v1/tenants/${integratorId}/games/${gameId}/rules?locale=${locale}`,
      { cache: 'no-store' },
    );
    if (!res.ok) return GENERIC_FALLBACK_MD;
    const data = (await res.json()) as { markdown?: string };
    return data.markdown ?? GENERIC_FALLBACK_MD;
  } catch {
    return GENERIC_FALLBACK_MD;
  }
}

/** Normalise whatever is stored as the `rules` surface value to the canonical
 *  `{ byLocale, default? }` shape. Returns `null` when there is no stored value. */
function normalizeStoredValue(raw: unknown): RulesOverride | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // New shape — already correct.
  if (r.byLocale && typeof r.byLocale === 'object') {
    return {
      byLocale: r.byLocale as Record<string, string>,
      default: typeof r.default === 'string' ? r.default : undefined,
    };
  }
  // Legacy shape { markdown, locale } → normalise in-place.
  if (typeof r.markdown === 'string') {
    const loc = typeof r.locale === 'string' ? r.locale : 'en';
    return { byLocale: { [loc]: r.markdown }, default: loc };
  }
  return null;
}

/** Minimal markdown → HTML renderer: headings, bold, italic, code, lists, paragraphs. */
function renderMarkdown(src: string): string {
  const lines = src.split('\n');
  const out: string[] = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inList) {
        out.push('</ul>');
        inList = false;
      }
      continue;
    }
    let m = line.match(/^###\s+(.*)$/);
    if (m) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h3>${escapeHtml(m[1] ?? '')}</h3>`);
      continue;
    }
    m = line.match(/^##\s+(.*)$/);
    if (m) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h2>${escapeHtml(m[1] ?? '')}</h2>`);
      continue;
    }
    m = line.match(/^#\s+(.*)$/);
    if (m) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h1>${escapeHtml(m[1] ?? '')}</h1>`);
      continue;
    }
    m = line.match(/^[-*]\s+(.*)$/);
    if (m) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inlineMd(m[1] ?? '')}</li>`);
      continue;
    }
    if (inList) { out.push('</ul>'); inList = false; }
    out.push(`<p>${inlineMd(line)}</p>`);
  }
  if (inList) out.push('</ul>');
  return out.join('\n');
}

function inlineMd(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

export async function renderRulesPage(root: HTMLElement, deps: RulesPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Rules Panel — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Overview markdown rendered in the player-facing Rules tab, authored per locale. Payout figures come from the Payouts tab (live config), so keep this to prose. Public read endpoint at <code>${deps.roundEngineBaseUrl}/v1/tenants/${deps.integratorId}/games/${deps.gameId}/rules?locale=en</code>. Two-admin approval required.</p>

    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:flex-start;gap:16px">
        <div style="flex:1;min-width:0">
          <div id="rules-locale-switcher" style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px"></div>
          <h2 style="margin-top:0">Markdown source</h2>
          <textarea id="rules-source" style="width:100%;min-height:360px;background:var(--panel-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius);padding:10px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px"></textarea>
          <div class="row" style="margin-top:10px">
            <button id="rules-save" class="primary">Propose change</button>
            <button id="rules-reset" class="ghost">Reset to defaults</button>
            <span id="rules-msg" class="muted"></span>
          </div>
        </div>
        <div style="flex:1;min-width:0">
          <h2>Live preview</h2>
          <div id="rules-preview" class="diff" style="min-height:360px;background:var(--panel-2);font-family:inherit;font-size:14px;white-space:normal;color:var(--text)"></div>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="rules-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const sourceEl = root.querySelector<HTMLTextAreaElement>('#rules-source')!;
  const previewEl = root.querySelector<HTMLElement>('#rules-preview')!;
  const msgEl = root.querySelector<HTMLElement>('#rules-msg')!;
  const switcherEl = root.querySelector<HTMLElement>('#rules-locale-switcher')!;

  // ── In-memory model ─────────────────────────────────────────────────────────
  // byLocale: authored overrides for each locale (empty string = not authored)
  const byLocale: Record<string, string> = {};
  // localeDefaults: per-locale defaults fetched from the round-engine endpoint
  const localeDefaults: Record<string, string> = {};

  let activeLoc: SupportedLocale = 'en';

  // ── Live preview ─────────────────────────────────────────────────────────────
  function updatePreview(): void {
    previewEl.innerHTML = renderMarkdown(sourceEl.value);
  }
  sourceEl.addEventListener('input', updatePreview);

  // ── Locale switcher ───────────────────────────────────────────────────────────
  function renderSwitcher(): void {
    switcherEl.innerHTML = '';
    for (const loc of LOCALES) {
      const isAuthored = typeof byLocale[loc] === 'string' && byLocale[loc].trim().length > 0;
      const isActive = loc === activeLoc;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.style.cssText = [
        'display:flex;align-items:center;gap:5px;padding:5px 12px',
        `border:1px solid ${isActive ? 'var(--accent,#6366f1)' : 'var(--border)'}`,
        `border-radius:var(--radius)`,
        `background:${isActive ? 'var(--accent,#6366f1)' : 'var(--panel-2)'}`,
        `color:${isActive ? '#fff' : 'var(--text)'}`,
        'font-size:13px;cursor:pointer',
      ].join(';');

      const badge = document.createElement('span');
      badge.textContent = isAuthored ? 'authored' : 'default';
      badge.style.cssText = [
        'font-size:10px;padding:1px 5px;border-radius:999px;font-weight:600',
        `background:${isAuthored ? (isActive ? 'rgba(255,255,255,0.25)' : 'var(--accent,#6366f1)') : (isActive ? 'rgba(255,255,255,0.15)' : 'var(--border)')}`,
        `color:${isAuthored && !isActive ? '#fff' : 'inherit'}`,
      ].join(';');

      btn.append(document.createTextNode(loc.toUpperCase()), badge);

      btn.addEventListener('click', () => {
        // Commit the textarea content back to byLocale for the current locale
        // before switching, so edits are not lost.
        byLocale[activeLoc] = sourceEl.value;
        activeLoc = loc as SupportedLocale;
        loadLocaleIntoTextarea();
        renderSwitcher();
      });

      switcherEl.appendChild(btn);
    }
  }

  /** Load the active locale's content into the textarea (authored if set, else default). */
  function loadLocaleIntoTextarea(): void {
    const authored = byLocale[activeLoc];
    sourceEl.value =
      typeof authored === 'string' && authored.length > 0
        ? authored
        : (localeDefaults[activeLoc] ?? GENERIC_FALLBACK_MD);
    updatePreview();
  }

  // ── Bootstrap: fetch defaults + load surface value ────────────────────────
  // Fetch defaults for all 5 locales concurrently.
  const defaultFetches = await Promise.all(
    LOCALES.map((loc) =>
      fetchDefaultRulesMarkdown(deps.roundEngineBaseUrl, deps.integratorId, deps.gameId, loc).then(
        (md) => [loc, md] as [string, string],
      ),
    ),
  );
  for (const [loc, md] of defaultFetches) {
    localeDefaults[loc] = md;
  }

  // Load stored surface value and normalise to { byLocale }.
  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'rules');
  const stored = normalizeStoredValue(current);
  if (stored) {
    for (const [loc, md] of Object.entries(stored.byLocale)) {
      if (typeof md === 'string' && md.trim()) byLocale[loc] = md;
    }
  }

  // Initial render.
  loadLocaleIntoTextarea();
  renderSwitcher();

  // ── Reset: current locale only ─────────────────────────────────────────────
  root.querySelector<HTMLButtonElement>('#rules-reset')!.addEventListener('click', () => {
    byLocale[activeLoc] = '';
    loadLocaleIntoTextarea();
    renderSwitcher();
    msgEl.textContent = `Reset ${activeLoc.toUpperCase()} to default (not saved).`;
    msgEl.className = 'muted';
  });

  // ── Propose: send full byLocale model ─────────────────────────────────────
  root.querySelector<HTMLButtonElement>('#rules-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    // Commit the current textarea before proposing.
    byLocale[activeLoc] = sourceEl.value;

    // Build the full payload — only include locales that have authored content.
    const payload: Record<string, string> = {};
    for (const loc of LOCALES) {
      if (typeof byLocale[loc] === 'string' && byLocale[loc].trim().length > 0) {
        payload[loc] = byLocale[loc];
      }
    }
    if (Object.keys(payload).length === 0) {
      msgEl.textContent = 'Nothing to propose — all locales are at default.';
      msgEl.className = 'muted';
      return;
    }

    const value: RulesOverride = { byLocale: payload, default: 'en' };
    const parsed = rulesSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'rules',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      renderSwitcher();
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  // ── Pending approvals ─────────────────────────────────────────────────────
  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'rules' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#rules-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;

    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — rules live.';
          msgEl.className = 'muted';
          await refreshPending();
          // Reload the surface and update byLocale so badges reflect the live state.
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'rules');
          const freshNorm = normalizeStoredValue(fresh.value);
          for (const loc of LOCALES) delete byLocale[loc];
          if (freshNorm) {
            for (const [loc, md] of Object.entries(freshNorm.byLocale)) {
              if (typeof md === 'string' && md.trim()) byLocale[loc] = md;
            }
          }
          loadLocaleIntoTextarea();
          renderSwitcher();
        } catch (err) {
          msgEl.textContent =
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });

    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent =
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
